import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import JobInfoDTO from '@src/adapters/dto/job/JobInfoDTO';
import useFindJobInformationLazy from '@src/adapters/hooks/job/useGetJobInformation';
import { JobBookingStatus } from '@src/constants/JobBookingStatus';
import { formatDate } from '@src/utils/dateUtils';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import useSaveJobDecisionLazy from '@src/adapters/hooks/job/useSaveJobDecision';
import UserAvailabilityDTO from '@src/adapters/dto/user/UserAvailabilityDTO';

interface Props {
  availability: UserAvailabilityDTO;
  open: boolean;
  close(status?: JobBookingStatus): void;
}

function JobPreview({ availability, close, open }: Props) {
  const { execute: getjob } = useFindJobInformationLazy();
  const { execute: saveDecision } = useSaveJobDecisionLazy();

  const [job, setjob] = useState<JobInfoDTO>();

  useEffect(() => {
    const init = async () => {
      if (availability.jobId) {
        const apiResult = await getjob({ id: availability.jobId });
        setjob(apiResult.job);
      }
    };

    init();
  }, [getjob, availability.jobId]);

  const updateDecision = async (accept: boolean) => {
    const result = await saveDecision({
      availabilityId: availability.id!,
      jobId: availability.jobId!,
      accept,
    });

    close(result.status ? result.status : JobBookingStatus.Unassigned);
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="Job Details"
      aria-describedby="Job Details"
      fullWidth={true}
      maxWidth={'xs'}>
      <DialogTitle id="job-details-title">
        <Grid
          container
          spacing={2}
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <Grid size={10}>
            <Typography variant="h6">
              {formatDate(availability.date, 'dddd DD MMMM')}
            </Typography>
            <Typography
              variant="subtitle1"
              color={
                availability.jobStatus === JobBookingStatus.Pencilled
                  ? 'warning.main'
                  : 'success.main'
              }>
              {availability.jobStatus}
            </Typography>
          </Grid>
          <Grid size={2}>
            <IconButton
              aria-label="close"
              color="secondary"
              onClick={() => close()}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <>
        <DialogContent
          sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="body1" color="textSecondary">
                Production Company
              </Typography>
              <Typography variant="body1" color="textPrimary">
                {job?.productionCompany}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="body1" color="textSecondary">
                Production
              </Typography>
              <Typography variant="body1" color="textPrimary">
                {job?.production}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="subtitle1">Job Details</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="body1" color="textSecondary">
                Location
              </Typography>
              <Typography variant="body1" color="textPrimary">
                {job?.location}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="body1" color="textSecondary">
                Additional Information
              </Typography>
              <Typography variant="body1" color="textPrimary">
                {job?.additionalInfo}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="body1" color="textSecondary">
                Fee
              </Typography>
              <Typography variant="body1" color="textPrimary">
                £{job?.fee}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
      </>
      <DialogActions sx={{ flex: '1', marginRight: '8px' }}>
        {availability.jobStatus !== JobBookingStatus.Confirmed &&
          availability.jobStatus !== JobBookingStatus.PencilAccepted && (
            <>
              <Button
                onClick={() => updateDecision(false)}
                color="error"
                variant="outlined"
                sx={{ flex: '1', marginLeft: '8px' }}>
                Reject
              </Button>
              <Button
                onClick={() => updateDecision(true)}
                color="primary"
                variant="contained"
                sx={{ flex: '1', marginLeft: '8px' }}>
                Accept
              </Button>
            </>
          )}
      </DialogActions>
    </Dialog>
  );
}

export default JobPreview;
